/* Modulo: Product Coverflow — JS owns the active index; CSS owns the depth.
   Click/tap a side card to bring it forward, drag to swipe, arrow keys on the
   focused stage, optional arrows + dots, loop wrap, live-region announcement,
   theme-editor reload safe. Vanilla IIFE, no deps.
   Clone slots: a store with fewer products than the fan needs renders extra
   slots carrying `data-index` = product i mod total, so dots (one per REAL
   product) map to the nearest slot showing that product. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var SWIPE = 46;

  function init(root) {
    if (root.dataset.moduloPcvReady === '1') return;
    root.dataset.moduloPcvReady = '1';

    var stage = root.querySelector('[data-stage]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-card]'));
    var total = cards.length;
    if (!stage || total < 2) return;

    var loop = root.dataset.loop === '1';
    var eachSide = parseInt(root.dataset.eachSide, 10) || 3;
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-dot]'));
    var live = root.querySelector('[data-live]');
    var rtl = (window.getComputedStyle(root).direction === 'rtl');
    var active = 0;

    function shortest(i) {
      var raw = i - active;
      if (loop) {
        if (raw > total / 2) raw -= total;
        if (raw < -total / 2) raw += total;
      }
      return raw;
    }

    function render() {
      var activeProduct = parseInt(cards[active].dataset.index, 10) || 0;
      for (var i = 0; i < total; i++) {
        var el = cards[i];
        var off = shortest(i);
        var ao = off < 0 ? -off : off;
        var isC = off === 0;
        el.style.setProperty('--off', off);
        el.style.setProperty('--aoff', ao);
        el.style.zIndex = 40 - ao;
        el.dataset.hidden = ao > eachSide ? '1' : '0';
        el.classList.toggle('is-active', isC);
        el.setAttribute('aria-hidden', isC ? 'false' : 'true');
        // Only the centre card's links are tabbable; the photo link never is
        // (it is aria-hidden — the title names the product).
        var links = el.querySelectorAll('[data-link]');
        for (var k = 0; k < links.length; k++) {
          if (isC && !links[k].classList.contains('modulo-pcv__photo')) links[k].removeAttribute('tabindex');
          else links[k].setAttribute('tabindex', '-1');
        }
      }
      for (var d = 0; d < dots.length; d++) {
        if (d === activeProduct) dots[d].setAttribute('aria-current', 'true');
        else dots[d].removeAttribute('aria-current');
      }
      if (live) {
        live.textContent = 'Product ' + (activeProduct + 1) + ' of ' + (dots.length || root.dataset.total || total) + ': ' + (cards[active].getAttribute('data-label') || '');
      }
    }

    function goTo(i) {
      var t = loop ? ((i % total) + total) % total : Math.max(0, Math.min(total - 1, i));
      if (t === active) return;
      active = t;
      render();
    }
    function next() { goTo(active + 1); }
    function prev() { goTo(active - 1); }

    // Dot d → the slot showing product d that is nearest to the current centre.
    function goToProduct(d) {
      var best = -1, bestDist = Infinity;
      for (var i = 0; i < total; i++) {
        if ((parseInt(cards[i].dataset.index, 10) || 0) !== d) continue;
        var dist = Math.abs(shortest(i));
        if (dist < bestDist) { bestDist = dist; best = i; }
      }
      if (best >= 0) goTo(best);
    }

    cards.forEach(function (el, i) {
      el.addEventListener('click', function (e) {
        if (el.classList.contains('is-active')) return;
        if (e.detail === 0) return; // keyboard activation of a link: let it navigate
        e.preventDefault();
        goTo(i);
      });
      el.addEventListener('focusin', function () {
        if (!el.classList.contains('is-active')) goTo(i);
      });
    });

    dots.forEach(function (b, d) {
      b.addEventListener('click', function () { goToProduct(d); });
    });

    function bind(list, fn) {
      for (var i = 0; i < list.length; i++) {
        list[i].addEventListener('click', function (e) { e.preventDefault(); fn(); });
      }
    }
    bind(root.querySelectorAll('[data-prev]'), prev);
    bind(root.querySelectorAll('[data-next]'), next);

    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); rtl ? next() : prev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); rtl ? prev() : next(); }
      else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); goTo(total - 1); }
    });

    // pointer drag / swipe (one step per gesture)
    var sx = 0, drag = false;
    stage.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      drag = true; sx = e.clientX;
    });
    function endDrag(e) {
      if (!drag) return;
      drag = false;
      var dx = (e && e.clientX != null ? e.clientX : sx) - sx;
      if (Math.abs(dx) > SWIPE) {
        if (rtl) dx = -dx;
        dx < 0 ? next() : prev();
      }
    }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', function () { drag = false; });
    stage.addEventListener('pointerleave', function (e) { if (drag) endDrag(e); });

    root._pcvDestroy = function () { root.dataset.moduloPcvReady = ''; };

    render();
  }

  function initAll(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-pcv]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // An identical <script src> still executes once per tag: guard the document listeners.
  if (!window.moduloPcvWired) {
    window.moduloPcvWired = true;
    document.addEventListener('shopify:section:load', function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-pcv]') : null;
      if (el) init(el);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-pcv]') : null;
      if (el && typeof el._pcvDestroy === 'function') el._pcvDestroy();
    });
  }
})();
